import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUpDownIcon, CheckIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { shopsApi } from '../../utils/api';
import { obtenirInitiales } from '../../utils/initiales';
import useFermetureExterieure from '../../hooks/useFermetureExterieure';
import './ShopSwitcher.css';

/**
 * Permet au marchand de choisir la boutique active. Le choix est mémorisé dans
 * le navigateur et transmis dans les appels API par l'en-tête X-Shop-Id.
 *
 * Rendu sous forme de menu déroulant personnalisé (et non plus d'un <select>
 * natif) afin de s'accorder au thème du conteneur via les tokens `--dl-*`.
 * @param {boolean} [compact=false] - Variante réduite (en-tête mobile) :
 *   sans libellé « Boutique », menu aligné à droite du déclencheur.
 */
const ShopSwitcher = ({ compact = false }) => {
  const navigate = useNavigate();
  const [shops, setShops] = useState([]);
  const [activeShopId, setActiveShopId] = useState(() => window.localStorage.getItem('activeShopId') || '');
  // Ouverture du menu déroulant
  const [estOuvert, setEstOuvert] = useState(false);
  // Conteneur servant à détecter les clics à l'extérieur du menu
  const conteneurRef = useRef(null);

  /**
 * Charge les boutiques accessibles sans jamais imposer un choix automatique.
 * L'écran de sélection dédié s'affiche lorsqu'aucune boutique n'est mémorisée.
   */
  useEffect(() => {
    const loadShops = async () => {
      try {
        const response = await shopsApi.getAll({ is_active: true });
        const availableShops = response.data || [];
        setShops(availableShops);
        if (activeShopId && !availableShops.some((shop) => shop.id === activeShopId)) {
          window.localStorage.removeItem('activeShopId');
          setActiveShopId('');
        }
      } catch (error) {
        console.error('Erreur lors du chargement des boutiques actives:', error);
      }
    };

    loadShops();
  }, [activeShopId]);

  // Ferme le menu au clic extérieur ou à la touche Échap (hook partagé)
  const fermerMenu = useCallback(() => setEstOuvert(false), []);
  useFermetureExterieure(conteneurRef, estOuvert, fermerMenu);

  /**
   * Mémorise la boutique choisie puis recharge le tableau de bord. Ainsi,
   * chaque liste et indicateur est relu avec le nouvel en-tête X-Shop-Id.
   * @param {string} shopId - Identifiant de la boutique sélectionnée
   */
  const handleShopChange = (shopId) => {
    setEstOuvert(false);
    // Re-sélectionner la boutique déjà active ne déclenche aucun rechargement
    if (shopId === activeShopId) return;

    window.localStorage.setItem('activeShopId', shopId);
    setActiveShopId(shopId);
    window.dispatchEvent(new CustomEvent('active-shop-changed', { detail: { shopId } }));
    window.location.assign('/app/dashboard');
  };

  /**
   * Ouvre les actions du compte hors du contexte de la boutique courante.
   * Le menu se ferme avant la navigation pour rester cohérent au retour.
   */
  const openAccountShops = (targetPath) => {
    setEstOuvert(false);
    navigate(targetPath);
  };

  if (shops.length === 0 || !activeShopId) return null;

  const boutiqueActive = shops.find((shop) => shop.id === activeShopId);
  const nomActif = boutiqueActive?.name || 'Boutique';

  return (
    <div ref={conteneurRef} className={`shop-switcher ${compact ? 'shop-switcher--compact' : ''}`}>
      {/* Déclencheur : avatar d'initiales + nom complet (infobulle si tronqué) */}
      <button
        type="button"
        className="shop-switcher__trigger dashboard-focusable"
        onClick={() => setEstOuvert((precedent) => !precedent)}
        aria-haspopup="listbox"
        aria-expanded={estOuvert}
        aria-label={`Boutique active : ${nomActif}. Changer de boutique`}
        title={nomActif}
      >
        <span className="shop-switcher__avatar" aria-hidden="true">{obtenirInitiales(nomActif)}</span>
        <span className="shop-switcher__text">
          {!compact && <span className="shop-switcher__label">Boutique</span>}
          <span className="shop-switcher__name">{nomActif}</span>
        </span>
        <ChevronUpDownIcon aria-hidden="true" className="shop-switcher__chevron" />
      </button>

      {/* Liste des boutiques accessibles, la boutique active est cochée */}
      {estOuvert && (
        <div className="shop-switcher__menu">
          <ul className="shop-switcher__options" role="listbox" aria-label="Boutiques disponibles">
            {shops.map((shop) => {
              const estActive = shop.id === activeShopId;
              return (
                <li key={shop.id} role="option" aria-selected={estActive}>
                  <button
                    type="button"
                    className={`shop-switcher__option dashboard-focusable ${estActive ? 'is-active' : ''}`}
                    onClick={() => handleShopChange(shop.id)}
                    title={shop.name}
                  >
                    <span className="shop-switcher__avatar shop-switcher__avatar--sm" aria-hidden="true">
                      {obtenirInitiales(shop.name)}
                    </span>
                    <span className="shop-switcher__name">{shop.name}</span>
                    {estActive && <CheckIcon aria-hidden="true" className="shop-switcher__check" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="shop-switcher__actions">
            <button type="button" className="shop-switcher__action dashboard-focusable" onClick={() => openAccountShops('/app/select-shop')}>
              <Squares2X2Icon aria-hidden="true" /> Changer ou créer une boutique
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopSwitcher;
